import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod/v4";

import {
  GetRecordComposerDefinitionDetailDocument,
  type GetRecordComposerDefinitionDetailQuery,
  type GetRecordComposerDefinitionDetailQueryVariables,
} from "~/common/gqls";
import type { MutationRefetchQueries } from "~/common/types";
import { Button, Input, Label, MultiSelect } from "~/components/ui";
import {
  GetEvidenceSetsDocument,
  type GetEvidenceSetsQuery,
  type GetEvidenceSetsQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useEntityComposerDefinitionForm } from "~/features/dataImportProjects/hooks/useEntityComposerDefinitionForm";
import { useRelationComposerDefinitionForm } from "~/features/dataImportProjects/hooks/useRelationComposerDefinitionForm";
import {
  entityComposerJsonSchema,
  relationComposerJsonSchema,
  zEntityComposerDefinition,
  zRelationComposerDefinition,
} from "~/features/dataImportProjects/utils/composerDefinition";
import { PropertySchemasMapper } from "~/features/dataImportProjects/utils/propertySchemasMapper";
import { useTenant } from "~/features/tenant/context";
import { DefinitionDiffModal } from "./DefinitionDiffModal";
import { DefinitionMonacoEditor } from "./DefinitionMonacoEditor";

const ENTITY_KNOWN_KEYS = new Set([
  "categoryNames",
  "entityIdResolver",
  "evidenceFilter",
  "entity",
  "composerKeyExtras",
]);
const RELATION_KNOWN_KEYS = new Set([
  "categoryNames",
  "relationIdResolver",
  "evidenceFilter",
  "sourceResolver",
  "targetResolver",
  "relation",
  "composerKeyExtras",
]);

type ComposerDetailData = GetRecordComposerDefinitionDetailQuery | undefined;

type ComposerEditFormProps = {
  importProjectId: string;
  evidenceSetIds: string[];
  recordComposerDefinitionId: string | null;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onSubmit: () => void;
};

/** Shared evidence-set selection + property-schema-set derivation used by both composer forms. */
function useComposerEvidenceSets(
  importProjectId: string,
  tenantId: string,
  evidenceSetIds: string[],
  data: ComposerDetailData,
) {
  const [selectedEvidenceSetIds, setSelectedEvidenceSetIds] = useState<string[]>(evidenceSetIds);

  useEffect(() => {
    setSelectedEvidenceSetIds(evidenceSetIds);
  }, [evidenceSetIds]);

  const { data: evidenceSetsData } = useSuspenseQuery<GetEvidenceSetsQuery, GetEvidenceSetsQueryVariables>(
    GetEvidenceSetsDocument,
    {
      variables: {
        tenantId,
        where: { evidenceSetImportProjects: { dataImportProjectId: { _eq: importProjectId } } },
      },
    },
  );

  const evidenceSets = evidenceSetsData?.evidenceSets ?? [];
  const selectedEvidenceSets = useMemo(
    () => evidenceSets.filter((evidenceSet) => selectedEvidenceSetIds.includes(evidenceSet.id)),
    [evidenceSets, selectedEvidenceSetIds],
  );

  const selectedEvidenceSchemaSet = useMemo(() => {
    const firstEvidenceSchemaSet = selectedEvidenceSets[0]?.evidenceSetPropertySchemaSet?.propertySchemaSet;
    if (firstEvidenceSchemaSet) {
      return {
        id: firstEvidenceSchemaSet.id,
        name: selectedEvidenceSets[0].name,
        propertySchemaAssociations: firstEvidenceSchemaSet.propertySchemaAssociations.map(
          PropertySchemasMapper.propertySchemaAssociation.toFormData,
        ),
      };
    }

    const rcdPss =
      data?.recordComposerDefinitions?.[0]?.recordComposerDefinitionPropertySchemaSet?.[0]?.propertySchemaSet;
    if (rcdPss) {
      return {
        id: rcdPss.id,
        name: data?.recordComposerDefinitions?.[0]?.name ?? "",
        propertySchemaAssociations: rcdPss.propertySchemaAssociations.map(
          PropertySchemasMapper.propertySchemaAssociation.toFormData,
        ),
      };
    }
    return undefined;
  }, [selectedEvidenceSets, data]);

  return { selectedEvidenceSetIds, setSelectedEvidenceSetIds, evidenceSets, selectedEvidenceSchemaSet };
}

type JsonManager = {
  editorJsonString: string;
  updateEditorString: (json: string) => void;
  hasChanges: boolean;
  revertToRaw: () => void;
  isEditorJsonValid: boolean;
  rawJson: Record<string, unknown>;
  workingJson: Record<string, unknown>;
};

/** Shared presentational fields: name, evidence-set multi-select, definition editor, save + diff modal. */
function ComposerFormFields({
  name,
  setName,
  evidenceSetOptions,
  selectedEvidenceSetIds,
  onSelectedChange,
  jsonManager,
  jsonSchema,
  schemaUri,
  processing,
  zodSchema,
  knownKeys,
  onConfirmUpsert,
}: {
  name: string;
  setName: (next: string) => void;
  evidenceSetOptions: { value: string; label: string }[];
  selectedEvidenceSetIds: string[];
  onSelectedChange: (next: string[]) => void;
  jsonManager: JsonManager;
  jsonSchema: Record<string, unknown>;
  schemaUri: string;
  processing: boolean;
  zodSchema: z.ZodType;
  knownKeys: Set<string>;
  onConfirmUpsert: () => void;
}) {
  const { t } = useTranslation();
  const [diffModalOpen, setDiffModalOpen] = useState(false);

  const handleSave = () => {
    if (!name.trim()) {
      return;
    }
    setDiffModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="composer-name">{t("model.common.name")}</Label>
        <Input id="composer-name" value={name} onChange={(event) => setName(event.target.value)} required />
      </div>

      <div className="space-y-1.5">
        <Label>{t("model.evidenceSet.modelNames")}</Label>
        <MultiSelect
          label={t("model.evidenceSet.modelNames")}
          options={evidenceSetOptions}
          selected={selectedEvidenceSetIds}
          onChange={onSelectedChange}
        />
      </div>

      {jsonManager.hasChanges ? (
        <div className="flex justify-end border-b border-border-default pb-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (window.confirm(t("ui.discardAllChanges"))) {
                jsonManager.revertToRaw();
              }
            }}
          >
            {t("ui.revertToOriginal")}
          </Button>
        </div>
      ) : null}

      <DefinitionMonacoEditor
        value={jsonManager.editorJsonString}
        onChange={jsonManager.updateEditorString}
        jsonSchema={jsonSchema}
        schemaUri={schemaUri}
      />

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={processing || !name.trim() || !jsonManager.isEditorJsonValid}
          onClick={handleSave}
        >
          {t("ui.save")}
        </Button>
      </div>

      <DefinitionDiffModal
        open={diffModalOpen}
        onClose={() => setDiffModalOpen(false)}
        onConfirm={() => {
          setDiffModalOpen(false);
          onConfirmUpsert();
        }}
        rawJson={jsonManager.rawJson}
        workingJson={jsonManager.workingJson}
        zodSchema={zodSchema}
        knownKeys={knownKeys}
      />
    </div>
  );
}

function useComposerDetailData(tenantId: string, recordComposerDefinitionId: string | null): ComposerDetailData {
  const queryResult = useSuspenseQuery<
    GetRecordComposerDefinitionDetailQuery,
    GetRecordComposerDefinitionDetailQueryVariables
  >(
    GetRecordComposerDefinitionDetailDocument,
    recordComposerDefinitionId ? { variables: { tenantId, id: recordComposerDefinitionId } } : skipToken,
  );
  return recordComposerDefinitionId ? queryResult.data : undefined;
}

function useEvidenceSetOptions(evidenceSets: GetEvidenceSetsQuery["evidenceSets"]) {
  const { t } = useTranslation();
  return useMemo(
    () =>
      evidenceSets.map((evidenceSet) => ({ value: evidenceSet.id, label: evidenceSet.name || `(${t("ui.noName")})` })),
    [evidenceSets, t],
  );
}

export function EntityComposerEditForm({
  importProjectId,
  evidenceSetIds,
  recordComposerDefinitionId,
  refetchQueriesOnEdit,
  onSubmit,
}: ComposerEditFormProps) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { name, setName, resetByEntityComposerDefinition, upsertEntityComposerDefinition, processing, jsonManager } =
    useEntityComposerDefinitionForm(onSubmit, refetchQueriesOnEdit);

  const data = useComposerDetailData(tenantId, recordComposerDefinitionId);
  const { selectedEvidenceSetIds, setSelectedEvidenceSetIds, evidenceSets, selectedEvidenceSchemaSet } =
    useComposerEvidenceSets(importProjectId, tenantId, evidenceSetIds, data);
  const evidenceSetOptions = useEvidenceSetOptions(evidenceSets);

  useEffect(() => {
    if (data) {
      resetByEntityComposerDefinition(data);
    }
  }, [data, resetByEntityComposerDefinition]);

  const composerDef = data?.recordComposerDefinitions?.[0];
  if (recordComposerDefinitionId && !composerDef) {
    throw new Error("Entity Composer not found");
  }
  const propertySchemaSetId = composerDef?.recordComposerDefinitionPropertySchemaSet?.[0]?.propertySchemaSet?.id;

  return (
    <ComposerFormFields
      name={name}
      setName={setName}
      evidenceSetOptions={evidenceSetOptions}
      selectedEvidenceSetIds={selectedEvidenceSetIds}
      onSelectedChange={setSelectedEvidenceSetIds}
      jsonManager={jsonManager}
      jsonSchema={entityComposerJsonSchema as Record<string, unknown>}
      schemaUri="entity-composer-definition-schema"
      processing={processing}
      zodSchema={zEntityComposerDefinition}
      knownKeys={ENTITY_KNOWN_KEYS}
      onConfirmUpsert={() =>
        upsertEntityComposerDefinition(
          importProjectId,
          selectedEvidenceSchemaSet,
          selectedEvidenceSetIds,
          composerDef?.id,
          propertySchemaSetId,
        )
      }
    />
  );
}

export function RelationComposerEditForm({
  importProjectId,
  evidenceSetIds,
  recordComposerDefinitionId,
  refetchQueriesOnEdit,
  onSubmit,
}: ComposerEditFormProps) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const {
    name,
    setName,
    resetByRelationComposerDefinition,
    upsertRelationComposerDefinition,
    processing,
    jsonManager,
  } = useRelationComposerDefinitionForm(onSubmit, refetchQueriesOnEdit);

  const data = useComposerDetailData(tenantId, recordComposerDefinitionId);
  const { selectedEvidenceSetIds, setSelectedEvidenceSetIds, evidenceSets, selectedEvidenceSchemaSet } =
    useComposerEvidenceSets(importProjectId, tenantId, evidenceSetIds, data);
  const evidenceSetOptions = useEvidenceSetOptions(evidenceSets);

  useEffect(() => {
    if (data) {
      resetByRelationComposerDefinition(data);
    }
  }, [data, resetByRelationComposerDefinition]);

  const composerDef = data?.recordComposerDefinitions?.[0];
  if (recordComposerDefinitionId && !composerDef) {
    throw new Error("Relation Composer not found");
  }
  const propertySchemaSetId = composerDef?.recordComposerDefinitionPropertySchemaSet?.[0]?.propertySchemaSet?.id;

  return (
    <ComposerFormFields
      name={name}
      setName={setName}
      evidenceSetOptions={evidenceSetOptions}
      selectedEvidenceSetIds={selectedEvidenceSetIds}
      onSelectedChange={setSelectedEvidenceSetIds}
      jsonManager={jsonManager}
      jsonSchema={relationComposerJsonSchema as Record<string, unknown>}
      schemaUri="relation-composer-definition-schema"
      processing={processing}
      zodSchema={zRelationComposerDefinition}
      knownKeys={RELATION_KNOWN_KEYS}
      onConfirmUpsert={() =>
        upsertRelationComposerDefinition(
          importProjectId,
          selectedEvidenceSchemaSet,
          selectedEvidenceSetIds,
          composerDef?.id,
          propertySchemaSetId,
        )
      }
    />
  );
}
